import { DateTime } from 'luxon';
import { t } from '@lingui/core/macro';
import { GQMeasurementProjectFootprintStatus } from '@watershed/shared-universal/generated/graphql-schema-types';

import { GQMeasurementTimelineForMeasurementPageFragment } from '@watershed/shared-universal/generated/graphql';

import {
  DATA_COLLECTION_DATA_PROCESSING_OFFSET_IN_DAYS,
  DATA_COLLECTION_MIN_LENGTH,
  FOOTPRINT_DEFAULT_LENGTH,
  REVIEW_AND_PROCESSING_SLA,
} from './timelineConstants';
import DateTimeUtils from '../utils/DateTimeUtils';
import { SupportedLocale } from '@watershed/intl/constants';

export function getAbsoluteDayDiff(start: DateTime, end: DateTime): number {
  return Math.abs(getDayDiff(start, end));
}

/**
 * Calculates days ago that day2 was compared to day1. A negative
 * result means that day2 is in the future compared to day1.
 **/
export function getDayDiff(day1: DateTime, day2: DateTime): number {
  return day1.diff(day2, 'days').days;
}

export function getMeasurementHeader(
  project: {
    active: boolean;
    measurementTimeline: GQMeasurementTimelineForMeasurementPageFragment;
    footprintStatus: GQMeasurementProjectFootprintStatus;
  },
  locale: SupportedLocale
): string {
  const deadline = DateTimeUtils.fromJSDate(
    project.measurementTimeline.deadline
  );
  const kickoff = DateTimeUtils.fromJSDate(project.measurementTimeline.kickoff);
  const today = DateTime.now().startOf('day');
  const isDeadlineInTheFuture = today < deadline;
  const isKickoffInTheFuture = today < kickoff;

  if (!project.active) {
    return t`Your measurement is complete!`;
  }

  // Your footprint is ready to review:
  if (project.footprintStatus === GQMeasurementProjectFootprintStatus.Draft) {
    return t`Your footprint is ready to review.`;
  }

  // planned for future
  if (isKickoffInTheFuture) {
    return t({
      message: `Let's do this`,
      comment: 'Refers to kicking off a measurement project',
    });
  }
  // Your deadline is today:
  if (today.hasSame(deadline, 'day')) {
    return t({
      message: `Your target is today.`,
      comment: '"Target" refers to the deadline of a project',
    });
  }
  // If the deadline is in the future and planning is complete:
  if (isDeadlineInTheFuture) {
    return t`Your measurement is in progress`;
  }

  // Your deadline has passed:
  const rtf = new Intl.RelativeTimeFormat(locale, {
    numeric: 'auto',
  });
  const missedBy = getAbsoluteDayDiff(deadline, today);
  const maxMissedDays = 30;
  if (missedBy > maxMissedDays) {
    return t({
      message: `You missed your target, but let's keep making progress.`,
      comment: '"Target" refers to the deadline of a project',
    });
  }
  const numDaysOverdue = rtf.format(-missedBy, 'day');
  return t({
    message: `Your target was ${numDaysOverdue}.`,
    comment: '"Target" refers to the deadline of a project',
  });
}

function getTimelineStepLengths(
  kickoff: DateTime,
  deadline: DateTime
): {
  dataCollectionDays: number;
  dataProcessingDays: number;
  footprintReviewDays: number;
} {
  const duration = deadline.diff(kickoff, 'days').days;
  const footprintReviewLength = Math.min(
    FOOTPRINT_DEFAULT_LENGTH,
    duration -
      DATA_COLLECTION_MIN_LENGTH -
      DATA_COLLECTION_DATA_PROCESSING_OFFSET_IN_DAYS
  );
  const remainingDays = duration - footprintReviewLength;

  return {
    dataCollectionDays: Math.max(
      remainingDays - REVIEW_AND_PROCESSING_SLA,
      DATA_COLLECTION_MIN_LENGTH
    ),
    dataProcessingDays:
      remainingDays - DATA_COLLECTION_DATA_PROCESSING_OFFSET_IN_DAYS,
    footprintReviewDays: footprintReviewLength,
  };
}

export function getTimelineStepLengthsWithDates(
  kickoff: Date,
  deadline: Date
): ReturnType<typeof getTimelineStepLengths> {
  return getTimelineStepLengths(
    DateTimeUtils.fromJSDate(kickoff),
    DateTimeUtils.fromJSDate(deadline)
  );
}
